import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectHoveredTempoElement,
  selectSelectedTempoElement,
  setSelectedElementKey,
  selectMultiSelectedTempoElements,
  setSelectedElementHTML,
  setMultiSelectedElementHTML,
  sendMessageToIFrame,
} from "../../../store/iframeSlice";
import { useChangeLedgerStore } from "../../../store/zustand/changeLedger";
import { toast } from "sonner";
import {
  useActiveCanvas,
  canDragIntoNavNode,
  isNullOrUndefined,
  getUnexpiredToken,
} from "@/utils/utils";
import { v4 as uuidv4 } from "uuid";
import { NavTreeNode } from "@/utils/types";

import {
  sendSyntheticMouseUp,
  useThrottledSyntheticMouseMove,
} from "@/utils/iframeUtils";
import { TempoElement } from "@/utils/tempoElement";
import {
  AddJsxChange,
  AnyChangeLedgerItem,
  MoveJsxChange,
} from "@/store/zustand/changeLedgerTypes";
import {
  getComponentFileFromExtractedElements,
  getExtractedElement,
} from "@/utils/extractElementsUtils";
import {
  useCanvasStore,
  useMultiselectedNavTreeNode,
  useSelectedNavTreeNode,
} from "@/store/zustand/canvasStore";
import { CANVAS_HEIGHT, CANVAS_WIDTH, getStoryboardForPoint } from "../utils";
import { useInitializeStoryboard } from "@/hooks/storyboards/useInitializeStoryboards";
import { useCreateStoryboard } from "@/hooks/storyboards/useCreateStoryboard";
import React from "react";
import { cn } from "@/lib/utils";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useCanvasId } from "@/utils/utils";
import { useSession } from "@clerk/clerk-react";

export const canDragInParent = (
  parentNavTreeNode: NavTreeNode,
  movingNode?: NavTreeNode
) => {
  if (
    movingNode &&
    movingNode.scope?.codebaseId !== parentNavTreeNode.scope?.codebaseId
  ) {
    return false;
  }

  // Bug fix Dec 14 -> can't move into an img tag
  if (!canDragIntoNavNode(parentNavTreeNode)) {
    return false;
  }

  let validParentForMoving = false;
  if (
    parentNavTreeNode.displayType === "block" &&
    parentNavTreeNode.children?.filter((child: NavTreeNode) => !child.floatVal)
      .length === 0
  ) {
    validParentForMoving = true;
  }

  if (parentNavTreeNode.displayType?.includes("flex")) {
    validParentForMoving = true;
  }

  return validParentForMoving;
};

/**
 * Gives the index inside the parent nav tree node to perform the operation in
 * if movingNode is given, then this is for movement, otherwise for adding
 */
export const getExpectedIndexForPos = (
  parentNavTreeNode: NavTreeNode,
  pageX: number,
  pageY: number,
  movingNode?: NavTreeNode,
  cachedCurrentIndex?: number,
  originalBoundingBoxesCache?: { [elementKey: string]: any },
  preDraggingIndex?: number
) => {
  const validParentForMoving = canDragInParent(parentNavTreeNode, movingNode);

  if (!validParentForMoving) {
    return { expectedIndex: null };
  }

  const boundingBoxCache = originalBoundingBoxesCache || {};

  let currentIndex = !isNullOrUndefined(cachedCurrentIndex)
    ? cachedCurrentIndex
    : movingNode
    ? parentNavTreeNode.children.indexOf(movingNode)
    : null;

  if (currentIndex === -1) {
    currentIndex = null;
  }

  // If this element was moved inside of this parent, then adjust the elements to the original ones
  // to get the bounding box caches correctly
  const newElementKeyToOriginal = {};
  if (
    currentIndex !== null &&
    currentIndex !== undefined &&
    preDraggingIndex !== null &&
    preDraggingIndex !== undefined &&
    currentIndex !== preDraggingIndex
  ) {
    const recordOriginalElementKey = (
      node: NavTreeNode,
      originalIndex: number
    ) => {
      const elementUniquePathSegments = node.tempoElement.uniquePath.split("-");
      const originalUniquePath =
        elementUniquePathSegments
          .slice(0, elementUniquePathSegments.length - 1)
          .join("-") + `-${originalIndex}`;

      const originalElementKey = new TempoElement(
        node.tempoElement.codebaseId,
        node.tempoElement.storyboardId,
        originalUniquePath
      ).getKey();
      newElementKeyToOriginal[node.tempoElement.getKey()] = originalElementKey;
    };

    if (preDraggingIndex < currentIndex) {
      for (let i = preDraggingIndex; i < currentIndex; i += 1) {
        const originalIndex = i + 1;
        const child = parentNavTreeNode.children[i];
        if (child) {
          recordOriginalElementKey(child, originalIndex);
        }
      }
    } else {
      for (let i = preDraggingIndex; i > currentIndex; i -= 1) {
        const originalIndex = i - 1;
        const child = parentNavTreeNode.children[i];
        if (child) {
          recordOriginalElementKey(child, originalIndex);
        }
      }
    }
  }

  // Filter for all valid children (anything that is not fixed or absolute & is in the same scope)
  const allValidChildren = new Set(
    parentNavTreeNode.children.filter((child: any, index: number) => {
      let inSameScope = true;
      if (currentIndex) {
        // @ts-expect-error TS18048
        inSameScope = movingNode.scope?.codebaseId === child.scope?.codebaseId;
      }

      const boundingBox =
        boundingBoxCache[child.tempoElement.getKey()] || child.pageBoundingBox;

      if (boundingBox?.pageX === undefined || boundingBox?.pageX === null) {
        return false;
      }

      return (
        child.positionType !== "fixed" &&
        child.positionType !== "absolute" &&
        inSameScope
      );
    })
  );

  if (allValidChildren.size === 0) {
    return { expectedIndex: 0, currentIndex };
  }

  const isRow =
    parentNavTreeNode.displayType?.includes("flex") &&
    // @ts-expect-error TS18048
    parentNavTreeNode.flexDirection.includes("row");
  const isReverse =
    parentNavTreeNode.displayType?.includes("flex") &&
    // @ts-expect-error TS18048
    parentNavTreeNode.flexDirection.includes("reverse");

  const isBeforeChild = (child: NavTreeNode) => {
    const originalElementKey =
      newElementKeyToOriginal[child.tempoElement.getKey()] ||
      child.tempoElement.getKey();

    const boundingBox =
      boundingBoxCache[originalElementKey] || child.pageBoundingBox;

    if (isRow) {
      if (isReverse) {
        return pageX > boundingBox?.pageX + boundingBox?.width / 2;
      } else {
        return pageX < boundingBox?.pageX + boundingBox?.width / 2;
      }
    } else {
      if (isReverse) {
        return pageY > boundingBox?.pageY + boundingBox?.height / 2;
      } else {
        return pageY < boundingBox?.pageY + boundingBox?.height / 2;
      }
    }
  };

  let expectedIndex = currentIndex;
  for (let i = 0; i < parentNavTreeNode.children.length; i += 1) {
    if (i === currentIndex) {
      continue;
    }

    const child = parentNavTreeNode.children[i];

    // Skip valid children, but still loop over them to get the correct index
    if (!allValidChildren.has(child)) {
      continue;
    }

    // If moving earlier in the list, need to go past the element to the before side
    // otherwise need to go past the element to move it
    // If there is no current index, just go along the children
    // @ts-expect-error TS18049
    if (isNullOrUndefined(currentIndex) || i < currentIndex) {
      if (isBeforeChild(child)) {
        expectedIndex = i;
        break;
      }
    } else {
      if (!isBeforeChild(child)) {
        expectedIndex = i;
        // Do not exit, as the index to drop in might be further down
      }
    }
  }

  if (isNullOrUndefined(expectedIndex)) {
    expectedIndex = parentNavTreeNode.children.length;
  }

  return { expectedIndex, currentIndex };
};

const SideEffectComponent: React.FC<any> = React.memo(
  ({
    preDraggingBoundingBoxes,
    setPreDraggingBoundingBoxes,
    preDraggingIndex,
    setPreDraggingIndex,
    dragDoneTrigger,
    setDragDoneTrigger,
    dragCancelTrigger,
    setDragCancelTrigger,
    isDraggingOutOfStoryboard,
    setIsDraggingOutOfStoryboard,
    newDraggingIndex,
    setNewDraggingIndex,
    iframeDraggingMouseStart,
    setIframeDraggingMouseStart,
  }) => {
    const canvasId = useCanvasId();

    const { session } = useSession();
    const dispatch = useDispatch<any>();

    const selectedElement: TempoElement = useSelector(
      selectSelectedTempoElement
    );
    const selectedElementRef = useRef(selectedElement);
    useEffect(() => {
      selectedElementRef.current = selectedElement;
    }, [selectedElement]);

    const multiselectedElements: TempoElement[] = useSelector(
      selectMultiSelectedTempoElements
    );
    const multiselectedElementsRef = useRef(multiselectedElements);
    useEffect(() => {
      multiselectedElementsRef.current = multiselectedElements;
    }, [multiselectedElements]);

    const hoveredElement: TempoElement = useSelector(selectHoveredTempoElement);

    const extractedComponentFile = useSelector(
      (state: any) => state.iframe.extractedComponentFile
    );
    const extractedComponentFilePath = useSelector(
      (state: any) => state.iframe.extractedComponentFilePath
    );
    const canvasMousePos = useCanvasStore((store) => store.canvasMousePos);
    const iframeMousePos = useCanvasStore((store) => store.iframeMousePos);

    const selectedNavTreeNode = useSelectedNavTreeNode();
    const multiselectedNavTreeNodes = useMultiselectedNavTreeNode();

    const treeElementLookup = useCodebaseStore(
      (state) => state.treeElementLookup
    );

    const addChange = useChangeLedgerStore((state) => state.addChange);
    const flushing = useChangeLedgerStore((state) => state.flushing);

    const cursorType = useSelector(
      (state: any) => state.globalSettings.cursorType
    );
    const cursorTypeRef = useRef();
    useEffect(() => {
      cursorTypeRef.current = cursorType;
    }, [cursorType]);

    const selectedElementHTML = useSelector(
      (state: any) => state.iframe.selectedElementHTML
    );

    const [newDraggingParentElementKey, setNewDraggingParentElementKey] =
      useState(null);

    // When creating a new storyboard -> can set the HTML instantly
    const addStoryboardIdToSetHTML = useCanvasStore(
      (store) => store.addStoryboardIdToSetHTML
    );

    const elementKeyToNavNodeLookup = useCanvasStore(
      (store) => store.elementKeyToNavNodeLookup
    );

    const initializeStoryboard = useInitializeStoryboard();
    const createStoryboard = useCreateStoryboard();

    // Project from DB
    const canvas = useActiveCanvas();

    const iframeMessagingPorts = useCanvasStore(
      (store) => store.iframeMessagingPorts
    );

    // Throttled events
    const throttledSyntheticMouseMove = useThrottledSyntheticMouseMove();

    const dragOutOfStoryboard = async (movingNode: NavTreeNode) => {
      const { offsetX, offsetY } = iframeDraggingMouseStart || {
        offsetX: 0,
        offsetY: 0,
      };
      const boundingBox =
        preDraggingBoundingBoxes[selectedElement.getKey()] ||
        movingNode?.pageBoundingBox;

      setIsDraggingOutOfStoryboard(false);

      // Clear the dragging into new parent indicators
      // TODO: Reset the element to the original position
      const port = iframeMessagingPorts[selectedElement.storyboardId];
      port.postMessage({
        type: "executeFunction",
        id: uuidv4(),
        payload: {
          functionName: "tempMoveToNewParent",
          storyboardId: selectedElement.storyboardId,
          args: [null, null, null, null, null, true],
        },
      });

      let fileContentsToUse = extractedComponentFile;
      let filePathToUse = extractedComponentFilePath;

      // If they haven't been loaded yet, make an API request to load them
      if (!extractedComponentFile || !extractedComponentFilePath) {
        const { extractedElement, error } = getExtractedElement(
          movingNode,
          treeElementLookup
        );
        if (error) {
          toast.error("Error extracting elements: " + error);
          return;
        }

        if (!extractedElement) {
          toast.error("Elements could not be found to be extracted");
          return;
        }

        try {
          const supabaseJWT = await getUnexpiredToken(session);

          const { newFileContents, dummyFilePath } =
            // @ts-expect-error TS2345
            await getComponentFileFromExtractedElements(canvasId, supabaseJWT, [
              extractedElement,
            ]);

          fileContentsToUse = newFileContents;
          filePathToUse = dummyFilePath;
        } catch (error) {
          toast.error("Error extracting elements: " + error);
          return;
        }
      }

      if (
        hoveredElement.isKnownElement() &&
        hoveredElement.storyboardId !== selectedElement.storyboardId
      ) {
        // Dragging from one storyboard to another
        const navTreeNode = elementKeyToNavNodeLookup[hoveredElement.getKey()];
        const codebaseIdToAddTo = navTreeNode.tempoElement.codebaseId;

        const change: AnyChangeLedgerItem = new AddJsxChange({
          codebaseIdToAddTo,
          fileContentsToSourceFrom: fileContentsToUse,
          fileContentsSourceFilename: filePathToUse,
          htmlForInstantUpdate: selectedElementHTML,
        });

        dispatch(setSelectedElementKey(hoveredElement.getKey()));

        addChange(change);
        return;
      }

      const canvasState = useCanvasStore.getState();

      const left =
        canvasState.canvasMousePos.x + offsetX - boundingBox.width / 2;
      const top =
        canvasState.canvasMousePos.y + offsetY - boundingBox.height / 2;

      const newStoryboard = initializeStoryboard(
        left,
        top,
        boundingBox.width,
        boundingBox.height
      );

      createStoryboard({
        storyboard: newStoryboard,
        fileContentsToCreateWith: fileContentsToUse,
        // @ts-expect-error TS2345
        filePathToUse,
        actionText: "Dragged Component",
      });

      addStoryboardIdToSetHTML(newStoryboard.id, selectedElementHTML);
    };

    useEffect(() => {
      // Wait until flushing finishes before dropping
      if (flushing) {
        return;
      }

      if (dragCancelTrigger) {
        setDragCancelTrigger(false);

        // Reset state
        setIframeDraggingMouseStart(null);
        setNewDraggingIndex(null);
        setPreDraggingIndex(null);
        setPreDraggingBoundingBoxes({});
        setNewDraggingParentElementKey(null);
        setIsDraggingOutOfStoryboard(false);
      }

      if (dragDoneTrigger) {
        const movingNode = elementKeyToNavNodeLookup[selectedElement.getKey()];

        setDragDoneTrigger(false);

        // Reset state
        setIframeDraggingMouseStart(null);
        setNewDraggingIndex(null);
        setPreDraggingIndex(null);
        setPreDraggingBoundingBoxes({});
        setNewDraggingParentElementKey(null);

        if (!movingNode) {
          return;
        }

        if (isDraggingOutOfStoryboard) {
          dragOutOfStoryboard(movingNode);
          return;
        }

        if (newDraggingIndex === null) {
          return;
        }

        if (newDraggingParentElementKey === null) {
          return;
        }

        if (
          preDraggingIndex === newDraggingIndex &&
          // @ts-expect-error TS18048
          movingNode.parent.tempoElement.getKey() ===
            newDraggingParentElementKey
        ) {
          return;
        }

        const newParent =
          elementKeyToNavNodeLookup[newDraggingParentElementKey];

        const afterNode = newParent.children[newDraggingIndex - 1];
        const beforeNode =
          newParent.children[
            // @ts-expect-error TS18048
            movingNode.parent.tempoElement.getKey() ===
            newDraggingParentElementKey
              ? newDraggingIndex + 1
              : newDraggingIndex
          ];

        const moveJsxChange = new MoveJsxChange({
          codebaseIdToMoveTo: newParent.tempoElement.codebaseId,
          codebaseIdToMove: movingNode.tempoElement.codebaseId,
          afterCodebaseId: afterNode?.tempoElement.codebaseId,
          beforeCodebaseId: beforeNode?.tempoElement.codebaseId,
          expectedCurrentParentCodebaseId:
            // @ts-expect-error TS18048
            movingNode.parent.tempoElement.codebaseId,
        });

        // Don't send the instant update if it has already been updated in canvas
        // which happens when the parent stays the same
        if (
          // @ts-expect-error TS18048
          movingNode.parent.tempoElement.getKey() ===
          newDraggingParentElementKey
        ) {
          moveJsxChange.doNotSendInstantUpdate();
        } else {
          // Make sure it's still selected based on the new expected position & parent
          const newSelectedElement = new TempoElement(
            movingNode.tempoElement.codebaseId,
            movingNode.tempoElement.storyboardId,
            newParent.tempoElement.uniquePath + "-" + newDraggingIndex
          );
          moveJsxChange.setSelectedElementsAfterInstantUpdate(
            newSelectedElement.getKey(),
            // @ts-expect-error TS2345
            null
          );
        }

        addChange(moveJsxChange);

        // Clear the dragging into new parent indicators
        const port = iframeMessagingPorts[selectedElement.storyboardId];
        port.postMessage({
          type: "executeFunction",
          id: uuidv4(),
          payload: {
            functionName: "tempMoveToNewParent",
            storyboardId: selectedElement.storyboardId,
            args: [null, null, null, null, null, true],
          },
        });
      }
    }, [dragDoneTrigger, dragCancelTrigger, flushing]);

    /**
     * If the mouse has moved and we are currently dragging inside an iframe,
     * synthetically create the mouse move event so we can drag even if the mouse is outside the iframe
     *
     * For dragging inside an iframe specifically
     */
    useEffect(() => {
      // Can't drag when multiselected
      if (multiselectedNavTreeNodes?.length) {
        return;
      }

      if (
        !selectedNavTreeNode ||
        !iframeDraggingMouseStart ||
        dragDoneTrigger ||
        dragCancelTrigger
      ) {
        return;
      }

      const { offsetX, offsetY } = iframeDraggingMouseStart;

      // First, check if the mouse has moved off a storyboard while still dragging
      const storyboardAtMousePoint = getStoryboardForPoint(
        canvas,
        canvasMousePos.x,
        canvasMousePos.y
      );

      if (storyboardAtMousePoint?.id !== selectedElement.storyboardId) {
        // Simulate the event inside the iframe off the screen
        // @ts-expect-error TS18047
        const storyboardMoving = canvas.storyboards.find(
          (sb: any) => sb.id === selectedElement.storyboardId
        );

        // @ts-expect-error TS18048
        const pointInIframeX = canvasMousePos.x - storyboardMoving.x;
        // @ts-expect-error TS18048
        const pointInIframeY = canvasMousePos.y - storyboardMoving.y;

        // Also update the mouse move event inside the iframe
        throttledSyntheticMouseMove.current(
          iframeMessagingPorts[selectedElement.storyboardId],
          selectedElement.storyboardId,
          pointInIframeX,
          pointInIframeY,
          1 // Primary button is pressed down
        );

        // These are the actual positions of the center of the element, as pageX & pageY represent the mouse position,
        // and offsetX and offsetY represent the offset to the center of the element
        const xWithOffset = pointInIframeX + offsetX;
        const yWithOffset = pointInIframeY + offsetY;

        // If the position of the center of the element is outside the bounds of the iframe, then move into drag out
        // of storyboard mode
        if (
          xWithOffset < 0 ||
          // @ts-expect-error TS18048
          xWithOffset > storyboardMoving.width ||
          yWithOffset < 0 ||
          // @ts-expect-error TS18048
          yWithOffset > storyboardMoving.height
        ) {
          if (!isDraggingOutOfStoryboard) {
            const port = iframeMessagingPorts[selectedElement.storyboardId];
            // Clear the outlines inside the iframe for dragging

            // Set the position back to the original one
            if (preDraggingIndex !== null && preDraggingIndex !== undefined) {
              port.postMessage({
                type: "executeFunction",
                id: uuidv4(),
                payload: {
                  functionName: "tempMoveElement",
                  storyboardId: selectedElement.storyboardId,
                  args: [selectedElement.getKey(), preDraggingIndex],
                },
              });
            }

            // Clear the dragging between siblings
            port.postMessage({
              type: "executeFunction",
              id: uuidv4(),
              payload: {
                functionName: "tempMoveToNewParent",
                storyboardId: selectedElement.storyboardId,
                args: [null, null, null, null, null, true],
              },
            });

            setIsDraggingOutOfStoryboard(true);
          }

          return;
        }
      }

      setIsDraggingOutOfStoryboard(false);
    }, [canvasMousePos]);

    /**
     * Use effect for moving children
     */
    useEffect(() => {
      // Stop applying any changes if flushing
      if (flushing) {
        return;
      }

      // Can't drag when multiselected
      if (multiselectedNavTreeNodes?.length) {
        return;
      }

      if (
        !selectedNavTreeNode ||
        !iframeDraggingMouseStart ||
        !iframeMousePos ||
        dragDoneTrigger ||
        dragCancelTrigger
      ) {
        return;
      }

      // This happens in the moment between the DOM tree being updated and the selected element being updated,
      // we want to make sure the right element is selected before attempting a move
      if (
        selectedNavTreeNode.tempoElement.codebaseId !==
        selectedElement.codebaseId
      ) {
        return;
      }

      const { offsetX, offsetY } = iframeDraggingMouseStart;
      const { pageX, pageY } = iframeMousePos;

      // These are the actual positions of the center of the element, as pageX & pageY represent the mouse position,
      // and offsetX and offsetY represent the offset to the center of the element
      const xWithOffset = pageX + offsetX;
      const yWithOffset = pageY + offsetY;

      const parentNavNode = selectedNavTreeNode.parent;

      // Determine which parent we are currently dragging into
      const isInParent = (parent: NavTreeNode, forInsert?: boolean) => {
        const boundingBoxToUse = forInsert
          ? preDraggingBoundingBoxes[parent.tempoElement.getKey()] ||
            parent.pageBoundingBox
          : parent.pageBoundingBox;

        let compareLeft = boundingBoxToUse.pageX;
        let compareTop = boundingBoxToUse.pageY;
        let compareRight = boundingBoxToUse.pageX + boundingBoxToUse.width;
        let compareBottom = boundingBoxToUse.pageY + boundingBoxToUse.height;

        // If this is moving an element into a new one, consider inserting only if it's past half to the middle
        if (forInsert) {
          compareLeft = compareLeft + boundingBoxToUse.width / 4;
          compareTop = compareTop + boundingBoxToUse.height / 4;
          compareRight = compareRight - boundingBoxToUse.width / 4;
          compareBottom = compareBottom - boundingBoxToUse.height / 4;
        }

        if (xWithOffset < compareLeft) {
          return false;
        }

        if (xWithOffset > compareRight) {
          return false;
        }

        if (yWithOffset < compareTop) {
          return false;
        }

        if (yWithOffset > compareBottom) {
          return false;
        }

        return true;
      };

      let parentDraggingInto = parentNavNode;
      while (parentDraggingInto && !isInParent(parentDraggingInto)) {
        parentDraggingInto = parentDraggingInto.parent;
      }
      if (!parentDraggingInto) {
        return;
      }

      // Once we have the parent, check if there are any children it should be inside instead
      // Note this means you can only move one level down at a time, which we found was more intuitive
      for (let i = 0; i < parentDraggingInto.children.length; i += 1) {
        const childToCheck = parentDraggingInto.children[i];
        if (childToCheck === selectedNavTreeNode) {
          continue;
        }

        if (
          isInParent(childToCheck, true) &&
          canDragInParent(childToCheck, selectedNavTreeNode)
        ) {
          parentDraggingInto = childToCheck;
          break;
        }
      }

      const clearAllDragging = () => {
        setNewDraggingIndex(null);
        setNewDraggingParentElementKey(null);

        const port = iframeMessagingPorts[selectedElement.storyboardId];

        // Clear the dragging between siblings
        port.postMessage({
          type: "executeFunction",
          id: uuidv4(),
          payload: {
            functionName: "tempMoveToNewParent",
            storyboardId: selectedElement.storyboardId,
            args: [null, null, null, null, null, true],
          },
        });
      };

      // Pick the bottom-most parent that can be dragging into
      const moveAllowedInParent = canDragInParent(
        parentDraggingInto,
        selectedNavTreeNode
      );

      if (!moveAllowedInParent) {
        clearAllDragging();
        return;
      }

      const isMovingOutOfParent = parentDraggingInto !== parentNavNode;

      const { expectedIndex, currentIndex } = getExpectedIndexForPos(
        parentDraggingInto,
        xWithOffset,
        yWithOffset,
        selectedNavTreeNode,

        // If moving out of the parent, there is no newDraggingIndex in the new parent
        isMovingOutOfParent ? null : newDraggingIndex,
        preDraggingBoundingBoxes,

        // If moving out of the parent, the node didn't exist in this parent originally
        isMovingOutOfParent ? null : preDraggingIndex
      );

      if (expectedIndex === null) {
        clearAllDragging();
        return;
      }

      // First move, record where it was originally
      if (preDraggingIndex === null || preDraggingIndex === undefined) {
        setPreDraggingIndex(currentIndex);

        // Cache the bounding boxes to be used for the original locations even if there are instant updates
        const newPreDraggingBoundingBoxes = {};
        // @ts-expect-error TS18048
        parentNavNode.children.forEach((child: any) => {
          newPreDraggingBoundingBoxes[child.tempoElement.getKey()] =
            child.pageBoundingBox;
        });
        setPreDraggingBoundingBoxes(newPreDraggingBoundingBoxes);
      }

      if (
        currentIndex !== expectedIndex ||
        newDraggingParentElementKey !== parentDraggingInto.tempoElement.getKey()
      ) {
        const port = iframeMessagingPorts[selectedElement.storyboardId];

        if (isMovingOutOfParent) {
          const isRow =
            parentDraggingInto.displayType?.includes("flex") &&
            // @ts-expect-error TS18048
            parentDraggingInto.flexDirection.includes("row");
          const isReverse =
            parentDraggingInto.displayType?.includes("flex") &&
            // @ts-expect-error TS18048
            parentDraggingInto.flexDirection.includes("reverse");

          // Determine the position of the indicator
          // @ts-expect-error TS18048
          const afterElement = parentDraggingInto.children[expectedIndex - 1];
          // @ts-expect-error TS2538
          const beforeElement = parentDraggingInto.children[expectedIndex];

          let primaryAxisVal, secondaryAxisVal;

          const primaryAxisKey = isRow ? "pageX" : "pageY";
          const secondaryAxisKey = isRow ? "pageY" : "pageX";
          const primaryAxisDimension = isRow ? "width" : "height";
          const secondaryAxisDimension = isRow ? "height" : "width";

          secondaryAxisVal =
            // @ts-expect-error TS18048
            parentDraggingInto.pageBoundingBox[secondaryAxisKey] +
            Math.max(
              // @ts-expect-error TS18048
              parentDraggingInto.pageBoundingBox[secondaryAxisDimension] -
                // @ts-expect-error TS2532
                selectedNavTreeNode.pageBoundingBox?.[secondaryAxisDimension],
              0
            ) /
              2;

          if (isReverse) {
            if (afterElement) {
              // @ts-expect-error TS18048
              primaryAxisVal = afterElement.pageBoundingBox[primaryAxisKey];
            } else {
              primaryAxisVal =
                // @ts-expect-error TS18048
                parentDraggingInto.pageBoundingBox[primaryAxisKey] +
                // @ts-expect-error TS18048
                parentDraggingInto.pageBoundingBox[primaryAxisDimension];
            }

            if (beforeElement) {
              primaryAxisVal =
                (primaryAxisVal +
                  beforeElement.pageBoundingBox[primaryAxisKey] +
                  beforeElement.pageBoundingBox[primaryAxisDimension]) /
                2;
            } else {
              primaryAxisVal =
                (primaryAxisVal +
                  // @ts-expect-error TS18048
                  parentDraggingInto.pageBoundingBox[primaryAxisKey]) /
                2;
            }
          } else {
            if (afterElement) {
              primaryAxisVal =
                // @ts-expect-error TS18048
                afterElement.pageBoundingBox[primaryAxisKey] +
                // @ts-expect-error TS18048
                afterElement.pageBoundingBox[primaryAxisDimension];
            } else {
              primaryAxisVal =
                // @ts-expect-error TS18048
                parentDraggingInto.pageBoundingBox[primaryAxisKey];
            }

            if (beforeElement) {
              primaryAxisVal =
                (primaryAxisVal +
                  beforeElement.pageBoundingBox[primaryAxisKey]) /
                2;
            } else {
              primaryAxisVal =
                (primaryAxisVal +
                  // @ts-expect-error TS18048
                  parentDraggingInto.pageBoundingBox[primaryAxisKey] +
                  // @ts-expect-error TS18048
                  parentDraggingInto.pageBoundingBox[primaryAxisDimension]) /
                2;
            }
          }

          const newX = isRow ? primaryAxisVal : secondaryAxisVal;
          const newY = !isRow ? primaryAxisVal : secondaryAxisVal;

          const amountScrollX = iframeMousePos
            ? iframeMousePos.pageX - iframeMousePos.clientX
            : 0;
          const amountScrolledY = iframeMousePos
            ? iframeMousePos.pageY - iframeMousePos.clientY
            : 0;

          // Dragging between siblings
          port.postMessage({
            type: "executeFunction",
            id: uuidv4(),
            payload: {
              functionName: "tempMoveToNewParent",
              storyboardId: selectedElement.storyboardId,
              args: [
                isRow
                  ? 2
                  : Math.min(
                      // @ts-expect-error TS2345
                      selectedNavTreeNode.pageBoundingBox?.width,
                      parentDraggingInto.pageBoundingBox?.width
                    ),
                !isRow
                  ? 2
                  : Math.min(
                      // @ts-expect-error TS2345
                      selectedNavTreeNode.pageBoundingBox?.height,
                      parentDraggingInto.pageBoundingBox?.height
                    ),

                // Need to provide these in terms of clientX & clientY
                newX - amountScrollX,
                newY - amountScrolledY,

                parentDraggingInto.tempoElement.getKey(),

                false,
              ],
            },
          });
        } else {
          // Dragging between siblings
          port.postMessage({
            type: "executeFunction",
            id: uuidv4(),
            payload: {
              functionName: "tempMoveElement",
              storyboardId: selectedElement.storyboardId,
              args: [selectedElement.getKey(), expectedIndex],
            },
          });

          // Clear the dragging between siblings
          port.postMessage({
            type: "executeFunction",
            id: uuidv4(),
            payload: {
              functionName: "tempMoveToNewParent",
              storyboardId: selectedElement.storyboardId,
              args: [null, null, null, null, null, true],
            },
          });
        }

        setNewDraggingIndex(expectedIndex);
        setNewDraggingParentElementKey(
          // @ts-expect-error TS2345
          parentDraggingInto.tempoElement.getKey()
        );
      }
    }, [
      selectedNavTreeNode,
      multiselectedNavTreeNodes,
      iframeDraggingMouseStart,
      iframeMousePos,
    ]);

    return <></>;
  }
);

const DraggingOutOfStoryboardOutline: React.FC<any> = React.memo(
  ({
    isDraggingOutOfStoryboard,
    preDraggingBoundingBoxes,
    iframeDraggingMouseStart,
  }) => {
    const selectedElement: TempoElement = useSelector(
      selectSelectedTempoElement
    );
    const selectedNavTreeNode = useSelectedNavTreeNode();

    const canvasMousePos = useCanvasStore((store) => store.canvasMousePos);

    const activeCanvas = useActiveCanvas();

    const maxZIndex = useMemo(() => {
      return activeCanvas?.storyboards
        ?.map((storyboard: any) => storyboard.zIndex)
        ?.reduce((a: any, b: any) => Math.max(a, b), 0);
    }, [activeCanvas]);

    /**
     * When dragging out of a storyboard, draw an outline for the element being dragged out
     */
    if (!isDraggingOutOfStoryboard || !preDraggingBoundingBoxes) {
      return;
    }

    const { offsetX, offsetY } = iframeDraggingMouseStart || {
      offsetX: 0,
      offsetY: 0,
    };
    const boundingBox =
      preDraggingBoundingBoxes[selectedElement.getKey()] ||
      selectedNavTreeNode?.pageBoundingBox;

    if (!boundingBox) {
      return;
    }

    const left = boundingBox
      ? canvasMousePos.x + offsetX - boundingBox.width / 2
      : 0;
    const top = boundingBox
      ? canvasMousePos.y + offsetY - boundingBox.height / 2
      : 0;

    return (
      <div
        className={cn(
          "border-[#4597F7] border-2 pointer-events-none",
          (!isDraggingOutOfStoryboard ||
            !preDraggingBoundingBoxes ||
            !boundingBox) &&
            "hidden"
        )}
        style={{
          position: "absolute",
          // Middle of the canvas is 0, 0
          left: CANVAS_WIDTH / 2 + left,
          top: CANVAS_HEIGHT / 2 + top,
          transformOrigin: "top left",

          width: boundingBox.width,
          height: boundingBox.height,

          zIndex: maxZIndex + 1000,
        }}
      />
    );
  }
);

export const useIframeDrag = () => {
  const dispatch = useDispatch<any>();

  const mouseOverStoryboardId = useCanvasStore(
    (store) => store.mouseOverStoryboardId
  );

  const selectedNavTreeNode = useSelectedNavTreeNode();
  const setOngoingChange = useChangeLedgerStore(
    (state) => state.setOngoingChange
  );

  const selectedElement: TempoElement = useSelector(selectSelectedTempoElement);
  const selectedElementRef = useRef(selectedElement);
  useEffect(() => {
    selectedElementRef.current = selectedElement;
  }, [selectedElement]);

  // Internal state related to dragging
  const [iframeDraggingMouseStart, setIframeDraggingMouseStart] =
    useState(null);
  const [newDraggingIndex, setNewDraggingIndex] = useState(null);
  const [preDraggingBoundingBoxes, setPreDraggingBoundingBoxes] = useState({});
  const [preDraggingIndex, setPreDraggingIndex] = useState(null);
  const [dragDoneTrigger, setDragDoneTrigger] = useState(false);
  const [dragCancelTrigger, setDragCancelTrigger] = useState(false);
  const [isDraggingOutOfStoryboard, setIsDraggingOutOfStoryboard] =
    useState(false);

  const onIframeDragStart = useCallback(
    (e: any, storyboardId: string, outerHTML: string) => {
      setOngoingChange(true);
      dispatch(setSelectedElementHTML(outerHTML));
      dispatch(setMultiSelectedElementHTML(null));
      setIframeDraggingMouseStart(e);
      setNewDraggingIndex(null);
      setPreDraggingIndex(null);
      setPreDraggingBoundingBoxes({});
    },
    []
  );

  const onIframeDragEnd = useCallback((e: any, storyboardId: string) => {
    setOngoingChange(false);
    setDragDoneTrigger(true);
  }, []);

  const onIframeDragCancel = useCallback((e: any, storyboardId: string) => {
    setOngoingChange(false);
    setDragCancelTrigger(true);
  }, []);

  const triggerEndDrag = useCallback(() => {
    if (
      selectedNavTreeNode &&
      iframeDraggingMouseStart &&
      mouseOverStoryboardId &&
      !dragDoneTrigger
    ) {
      const iframeMessagingPorts =
        useCanvasStore.getState().iframeMessagingPorts;

      sendSyntheticMouseUp(
        iframeMessagingPorts[selectedElementRef.current.storyboardId],
        selectedElementRef.current.storyboardId
      );
    }
  }, [
    selectedNavTreeNode,
    iframeDraggingMouseStart,
    mouseOverStoryboardId,
    dragDoneTrigger,
  ]);

  const triggerStartDrag = useCallback(() => {
    dispatch(
      sendMessageToIFrame({
        type: "executeFunction",
        storyboardId: selectedElementRef.current.storyboardId,
        payload: {
          functionName: "triggerDragStart",
          args: [],
        },
      })
    );
  }, []);

  const triggerCancelDrag = useCallback(() => {
    dispatch(
      sendMessageToIFrame({
        type: "executeFunction",
        payload: {
          functionName: "triggerDragCancel",
          storyboardId: selectedElementRef.current.storyboardId,
          args: [],
        },
      })
    );
  }, []);

  return {
    onIframeDragEnd,
    onIframeDragStart,
    onIframeDragCancel,
    triggerStartDrag,
    triggerCancelDrag,
    triggerEndDrag,

    isDraggingOutOfStoryboard,
    isDragging: Boolean(iframeDraggingMouseStart),

    SideEffectComponentInstance: (
      <SideEffectComponent
        iframeDraggingMouseStart={iframeDraggingMouseStart}
        setIframeDraggingMouseStart={setIframeDraggingMouseStart}
        newDraggingIndex={newDraggingIndex}
        setNewDraggingIndex={setNewDraggingIndex}
        preDraggingBoundingBoxes={preDraggingBoundingBoxes}
        setPreDraggingBoundingBoxes={setPreDraggingBoundingBoxes}
        preDraggingIndex={preDraggingIndex}
        setPreDraggingIndex={setPreDraggingIndex}
        dragDoneTrigger={dragDoneTrigger}
        setDragDoneTrigger={setDragDoneTrigger}
        dragCancelTrigger={dragCancelTrigger}
        setDragCancelTrigger={setDragCancelTrigger}
        isDraggingOutOfStoryboard={isDraggingOutOfStoryboard}
        setIsDraggingOutOfStoryboard={setIsDraggingOutOfStoryboard}
      />
    ),
    DragOutOutlineInstance: (
      <DraggingOutOfStoryboardOutline
        isDraggingOutOfStoryboard={isDraggingOutOfStoryboard}
        preDraggingBoundingBoxes={preDraggingBoundingBoxes}
        iframeDraggingMouseStart={iframeDraggingMouseStart}
      />
    ),
  };
};
